import { isEmpty } from 'lodash'
import { ReactElement, useContext, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import Dropdown from 'components/v2/Dropdown'
import IconButton from 'components/v2/IconButton'
import { BusOutlinedIcon } from 'components/Icons/BusOutlinedIcon'
import { DotsIcon } from 'components/Icons/DotsIcon'
import { ForbiddenIcon } from 'components/Icons/ForbiddenIcon'
import { ModalityIcon } from 'components/Icons/ModalityIcon'
import List from 'components/v2/List'
import { useLogin } from 'features/Login/hooks'
import { CancelTripsModal } from 'features/Trips/components/CancelTripsModal'
import { ChangeTripsVehicleModal } from 'features/Trips/components/ChangeTripsVehicleModal'
import {
  canCancelTrip,
  canChangePastTripVehicle,
  canChangeTripConfirmationModalities,
  canChangeTripVehicle
} from 'features/Users/utils'
import { canAddReservedModality, canRemoveReservedModality } from 'features/Trips/utils'
import { RemoveReservedConfirmationModalityModal } from 'features/Trips/components/RemoveReservedConfirmationModal'
import { AddReservedConfirmationModalityModal } from 'features/Trips/components/AddReservedConfirmationModalityModal'
import { GTTripV2, GTTripV2Status } from 'gql/types.generated'
import { canBeCancelled } from 'resolvers/trips'
import { SelectedTripsContext } from '..'

const hasAtLeastOneCancellableTrip = (trips: GTTripV2[]): boolean => {
  return trips.some((trip) => canBeCancelled(trip))
}

const hasAtLeastOneChangeableVehicle = (trips: GTTripV2[]): boolean => {
  return trips.some((trip) => trip.status !== GTTripV2Status.Completed)
}

const hasAtLeastOneChangeablePastVehicle = (trips: GTTripV2[]): boolean => {
  return trips.some((trip) => trip.status === GTTripV2Status.Completed)
}

export const TripsMultipleActions = (): ReactElement | null => {
  const {
    accessToken: { policies }
  } = useLogin()
  const { selectedTrips, setSelectedTrips } = useContext(SelectedTripsContext)
  const [isOpenDropdown, setIsOpenDropdown] = useState(false)
  const [isOpenCancelTripsModal, setIsOpenCancelTripsModal] = useState(false)
  const [isOpenChangeTripsVehicleModal, setIsOpenChangeTripsVehicleModal] = useState(false)
  const [isOpenRemoveReservedConfirmationModalityModal, setOpenRemoveReservedConfirmationModalityModal] =
    useState(false)
  const [isOpenAddReservedConfirmationModalityModal, setOpenAddReservedConfirmationModalityModal] = useState(false)
  const canCancel = canCancelTrip(policies)
  const canChangeVehicle = canChangeTripVehicle(policies)
  const canChangePastVehicle = canChangePastTripVehicle(policies)
  const canChangeModality = canChangeTripConfirmationModalities(policies)

  const isShowingMultipleActions = canCancel || canChangeVehicle || canChangePastVehicle || canChangeModality

  if (!isShowingMultipleActions) return null

  const isCancelTripEnabled = canCancel && hasAtLeastOneCancellableTrip(selectedTrips)
  const isChangeVehicleEnabled = canChangeVehicle && hasAtLeastOneChangeableVehicle(selectedTrips)
  const isChangePastVehicleEnabled = canChangePastVehicle && hasAtLeastOneChangeablePastVehicle(selectedTrips)
  const isRemoveReservedModalityEnabled =
    canChangeModality && !isEmpty(selectedTrips) && selectedTrips.some((trip) => canRemoveReservedModality(trip))
  const isAddReservedModalityEnabled =
    canChangeModality && !isEmpty(selectedTrips) && selectedTrips.some((trip) => canAddReservedModality(trip))

  const toggleDropdown = (): void => {
    setIsOpenDropdown(!isOpenDropdown)
  }

  const handleCancelTrip = (): void => {
    if (isCancelTripEnabled) {
      setIsOpenDropdown(false)
      setIsOpenCancelTripsModal(true)
    }
  }

  const handleChangeVehicle = (): void => {
    if (isChangeVehicleEnabled || isChangePastVehicleEnabled) {
      setIsOpenDropdown(false)
      setIsOpenChangeTripsVehicleModal(true)
    }
  }

  const handleRemoveReservedModality = (): void => {
    if (isRemoveReservedModalityEnabled) {
      setIsOpenDropdown(false)
      setOpenRemoveReservedConfirmationModalityModal(true)
    }
  }

  const handleAddReservedModality = (): void => {
    if (isAddReservedModalityEnabled) {
      setIsOpenDropdown(false)
      setOpenAddReservedConfirmationModalityModal(true)
    }
  }

  const handleSuccess = (): void => {
    setSelectedTrips([])
  }

  return (
    <>
      <Dropdown.Wrapper noPadding noIndicator right onClickAway={(): void => setIsOpenDropdown(false)}>
        <IconButton onClick={toggleDropdown} icon={<DotsIcon />} noBorder smallPadding />
        <Dropdown.Content open={isOpenDropdown} noIndicator left noOverflow>
          <List.Container>
            <List.Item action onClick={handleCancelTrip} disabled={!isCancelTripEnabled}>
              <List.Icon>
                <ForbiddenIcon />
              </List.Icon>
              <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
            </List.Item>
            <List.Item
              action
              onClick={handleChangeVehicle}
              disabled={!isChangeVehicleEnabled && !isChangePastVehicleEnabled}
            >
              <List.Icon>
                <BusOutlinedIcon />
              </List.Icon>
              <FormattedMessage id="trips.multipleActions.changeVehicle.label" defaultMessage="Change vehicle" />
            </List.Item>
            <List.Item action onClick={handleRemoveReservedModality} disabled={!isRemoveReservedModalityEnabled}>
              <List.Icon>
                <ModalityIcon />
              </List.Icon>
              <FormattedMessage
                id="trips.multipleActions.removeReservedModality.label"
                defaultMessage="Remove reserved modality"
              />
            </List.Item>
            <List.Item action onClick={handleAddReservedModality} disabled={!isAddReservedModalityEnabled}>
              <List.Icon>
                <ModalityIcon />
              </List.Icon>
              <FormattedMessage
                id="trips.multipleActions.addReservedModality.label"
                defaultMessage="Add reserved modality"
              />
            </List.Item>
          </List.Container>
        </Dropdown.Content>
      </Dropdown.Wrapper>
      {isOpenCancelTripsModal && (
        <CancelTripsModal
          trips={selectedTrips}
          closeModal={(): void => setIsOpenCancelTripsModal(false)}
          onSuccess={handleSuccess}
        />
      )}
      {isOpenChangeTripsVehicleModal && (
        <ChangeTripsVehicleModal
          trips={selectedTrips}
          closeModal={(): void => setIsOpenChangeTripsVehicleModal(false)}
          onSuccess={handleSuccess}
        />
      )}
      {isOpenRemoveReservedConfirmationModalityModal && (
        <RemoveReservedConfirmationModalityModal
          trips={selectedTrips.filter((trip) => canRemoveReservedModality(trip))}
          closeModal={(): void => setOpenRemoveReservedConfirmationModalityModal(false)}
          onSuccess={handleSuccess}
        />
      )}
      {isOpenAddReservedConfirmationModalityModal && (
        <AddReservedConfirmationModalityModal
          trips={selectedTrips.filter((trip) => canAddReservedModality(trip))}
          closeModal={(): void => setOpenAddReservedConfirmationModalityModal(false)}
          onSuccess={handleSuccess}
        />
      )}
    </>
  )
}
